import type { ActivityOverviewDomain, ActivityOverviewStatusFilter } from '@sparkapp/shared-types';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BreakdownBarList } from './BreakdownBarList';
import { activityListHref } from './activityListHref';

interface DomainSkillCoverageProps {
  domain: ActivityOverviewDomain;
  status: ActivityOverviewStatusFilter;
}

/**
 * Skills owned by the domain: primary-domain use (solid) stacked with use by
 * activities in other domains (light). Excluded skills are muted and listed last.
 */
export function DomainSkillCoverage({ domain, status }: DomainSkillCoverageProps) {
  const active = domain.skills.filter((s) => !s.isExcluded);
  const excluded = domain.skills.filter((s) => s.isExcluded);
  const covered = active.filter((s) => s.primaryCount > 0).length;

  return (
    <Card className="bg-pine-card border-pine-line shadow-pine-card rounded-pine-card">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-heading-4 text-pine-ink">Skill coverage</CardTitle>
        <span className="flex items-center gap-3 text-caption text-pine-ink-soft">
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-4 rounded-full bg-pine-pine" />
            activities in this domain
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-4 rounded-full bg-pine-l3" />
            activities in other domains using this skill
          </span>
        </span>
      </CardHeader>
      <CardContent className="space-y-3">
        {domain.skills.length === 0 ? (
          <p className="text-sm text-pine-ink-soft">No skills configured.</p>
        ) : (
          <BreakdownBarList
            rows={[...active, ...excluded].map((skill) => {
              const onlyCross = !skill.isExcluded && skill.primaryCount === 0 && skill.crossDomainCount > 0;
              return {
                key: String(skill.id),
                label: (
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="truncate">{skill.title}</span>
                    {skill.isExcluded && <Badge variant="secondary">Excluded</Badge>}
                    {onlyCross && (
                      <span className="shrink-0 text-xs font-medium text-amber-800">
                        ⚠ only cross-domain
                      </span>
                    )}
                  </span>
                ),
                primary: skill.primaryCount,
                stacked: skill.crossDomainCount,
                muted: skill.isExcluded,
                href: activityListHref(status, { skillId: skill.id }),
              };
            })}
          />
        )}
        <p className="text-caption text-pine-ink-soft">
          {covered} of {active.length} active skills have activities in this domain. Excluded skills
          are not used by the recommendation engine.
        </p>
      </CardContent>
    </Card>
  );
}
